import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import FlowBars from "../../components/director/module1/FlowBars";
import MetricCard from "../../components/director/module1/MetricCard";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import { useDirectorContext } from "@contexts/director";
import { formatBps, formatCents, formatDriverCause } from "../../utils/directorFormat";
import {
  companyQuery,
  currentCompetence,
  moduleFilters,
  scenarioLabel,
} from "./module1Utils";

const definitions = {
  weekly: {
    surface: "cashflow/weekly",
    title: "Fluxo projetado · 13 semanas",
    description: "Realizados, compromissos e previsões sem dupla contagem — sem fabricar saldo.",
  },
  critical: {
    surface: "critical-periods",
    title: "Períodos de pressão de fluxo",
    description: "Sem saldo declarado, destacamos semanas em que saídas superam entradas.",
  },
  causes: {
    surface: "causal-explanations",
    title: "O que pressiona o fluxo",
    description: "Drivers calculados por regra e ligados a evidências opacas do ledger.",
  },
};

const totalsFor = (series = []) => series.reduce((total, item) => ({
  inflow: total.inflow + item.actualInflowCents + item.forecastInflowCents,
  outflow: total.outflow + item.actualOutflowCents + item.committedOutflowCents + item.plannedUncommittedOutflowCents,
  net: total.net + item.netFlowCents,
}), { inflow: 0, outflow: 0, net: 0 });

const LiquidityView = ({ view = "weekly" }) => {
  const definition = definitions[view] || definitions.weekly;
  const { selectedCompanyId, filters } = useDirectorContext();
  const [scenario, setScenario] = useState("base");
  const query = useMemo(() => moduleFilters({
    from: currentCompetence(),
    months: 24,
    scenario,
    filters,
  }), [filters, scenario]);
  const surface = useDirectorSurface(definition.surface, query);
  const data = surface.data;
  const totals = totalsFor(data?.series);
  const criticalBars = (data?.items || []).map((item) => ({
    periodKey: item.start,
    netFlowCents: -item.pressureCents,
  }));
  const causalBars = (data?.items || []).slice(0, 10).map((item) => ({
    periodKey: item.periodKey,
    netFlowCents: -item.contributionCents,
  }));
  return <ModuleShell
    title={definition.title}
    description={definition.description}
    badge={`Regime A · ${scenarioLabel(scenario)}`}
  >
    <section className="director-module1__filters" aria-label="Filtros de liquidez">
      <label className="director-filter-field"><span>Cenário</span><select value={scenario} onChange={(event) => setScenario(event.target.value)}>
        <option value="base">Base</option>
        <option value="conservative">Conservador</option>
        <option value="stress">Estresse</option>
      </select></label>
      <nav className="director-module1__tabs" aria-label="Leituras de liquidez">
        <Link data-active={view === "weekly"} to={`/dashboard/director/liquidity/weekly${companyQuery(selectedCompanyId)}`}>13 semanas</Link>
        <Link data-active={view === "critical"} to={`/dashboard/director/liquidity/critical-periods${companyQuery(selectedCompanyId)}`}>Períodos</Link>
        <Link data-active={view === "causes"} to={`/dashboard/director/liquidity/causes${companyQuery(selectedCompanyId)}`}>Causas</Link>
        <Link to={`/dashboard/director/cashflow${companyQuery(selectedCompanyId)}`}>Mensal</Link>
      </nav>
    </section>
    <ModuleState {...surface} availability={data?.availability} onRetry={surface.reload} />
    {data && view === "weekly" && <>
      <section className="director-module1__metrics">
        <MetricCard label="Entradas" value={formatCents(totals.inflow)} note="13 semanas" tone="blue" />
        <MetricCard label="Saídas" value={formatCents(totals.outflow)} note="13 semanas" tone="orange" />
        <MetricCard label="Fluxo líquido" value={formatCents(totals.net)} note="Saldo não disponível" tone={totals.net < 0 ? "pressure" : "green"} />
      </section>
      <section className="director-module1__split">
        <article className="director-module1__panel director-module1__panel--bars"><header><div><span className="director-eyebrow">Fluxo semanal</span><h2>Entradas menos saídas</h2></div></header><FlowBars items={data.series || []} /></article>
        <aside className="director-module1__insight"><span className="director-eyebrow">Como interpretar</span><h2>Pressão não é caixa negativo</h2><p>Sem um saldo inicial declarado, a série mede somente o movimento líquido de cada semana.</p><div className="director-module1__callout">Cobertura: {formatBps(data.coverage?.valueBps)}</div><Link className="director-button" to={`/dashboard/director/liquidity/critical-periods${companyQuery(selectedCompanyId)}`}>Ver períodos de pressão</Link></aside>
      </section>
    </>}
    {data && view === "critical" && <section className="director-module1__split">
      <article className="director-module1__panel director-module1__panel--bars"><header><div><span className="director-eyebrow">Janelas adversas</span><h2>{data.items?.length || 0} período(s) identificado(s)</h2></div></header><FlowBars items={criticalBars} /></article>
      <aside className="director-module1__insight"><span className="director-eyebrow">Leitura permitida</span><h2>Saídas superam entradas</h2><p>O módulo não chama isso de falta de caixa. Declare um saldo inicial para habilitar a leitura de saldo projetado.</p><Link className="director-button" to={`/dashboard/director/liquidity/causes${companyQuery(selectedCompanyId)}`}>Entender as causas</Link></aside>
    </section>}
    {data && view === "causes" && <section className="director-module1__split">
      <article className="director-module1__panel"><header><div><span className="director-eyebrow">Drivers adversos</span><h2>Contribuição explicada</h2></div></header><FlowBars items={causalBars} /><div className="director-table-scroll"><table className="director-table"><thead><tr><th>Período</th><th>Causa</th><th>Contribuição</th><th>Cobertura</th></tr></thead><tbody>{(data.items || []).map((item) => <tr key={`${item.periodKey}-${item.driverRef}`}><td>{item.periodKey}</td><td>{formatDriverCause(item.cause)}</td><td>{formatCents(item.contributionCents)}</td><td>{formatBps(item.coverageBps)}</td></tr>)}</tbody></table></div></article>
      <aside className="director-module1__insight"><span className="director-eyebrow">Regra, não IA</span><h2>Cada causa aponta evidência</h2><p>O ranking usa centro, estado do título e período. O valor não explicado continua visível e não é atribuído por narrativa.</p><div className="director-module1__callout">{data.items?.[0] ? `Explicado: ${formatBps(data.items[0].coverageBps)}` : "Sem período adverso"}</div></aside>
    </section>}
  </ModuleShell>;
};

export default LiquidityView;
